import { useState } from "react";
import type { PlayerGoal } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { PlayerDevelopmentProfile } from "@/features/feedback/PlayerDevelopmentProfile";
import { GoalList } from "@/features/feedback/components/GoalList";
import { useCoachPlayerDevelopment } from "@/features/feedback/hooks/useDevelopmentProfile";
import { useCoachGoalMutations } from "../hooks/useCoachGoals";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Coach-side Player Development modal (M7) — opened from a Coach >
 * Roster row's "Development profile" button.
 *
 * Hosts the SHARED `<PlayerDevelopmentProfile>` painter with
 * `viewer={false}` — so it renders the coach-only Themes section
 * + linked accounts that the viewer Development sub-tab omits.
 *
 * Goal list: the painter's `goalsSlot` gets a coach-mode
 * `<GoalList>`. Status changes happen inline (a `<select>` →
 * `updateGoal`, no nested modal). But "+ New goal" / "Edit" need
 * the `GoalComposerModal`, which itself wraps `CoachModal` — and
 * two stacked `CoachModal`s would both grab Escape. So those two
 * bubble UP to the host (`RosterPage`): the host closes THIS
 * modal and opens the composer instead. One modal at a time,
 * legacy-faithful enough (the coach lands back on Roster after
 * the composer; they can re-open the dev modal).
 *
 * Delete shows a `<ConfirmDialog>` — no nested modal stacking.
 *
 * Coach recent-note/clip/playlist rows are read-only (no
 * `onOpen*`) — the coach surface isn't a playback context.
 */

export type CoachDevelopmentModalProps = {
  playerId: string;
  /** Display name for the modal title while the profile loads;
   * the painter re-renders the full header from the payload. */
  playerName: string;
  onClose: () => void;
  /** Open the goal composer in CREATE mode for this player —
   * the host closes this modal first (one-modal-at-a-time). */
  onNewGoal: (playerId: string) => void;
  /** Open the goal composer in EDIT mode — same one-modal rule. */
  onEditGoal: (goal: PlayerGoal) => void;
};

export function CoachDevelopmentModal({
  playerId,
  playerName,
  onClose,
  onNewGoal,
  onEditGoal,
}: CoachDevelopmentModalProps) {
  const devQuery = useCoachPlayerDevelopment(playerId);
  const goalMutations = useCoachGoalMutations();
  const [confirmDeleteGoal, setConfirmDeleteGoal] = useState<PlayerGoal | null>(null);

  const handleStatusChange = (goalId: number, status: string) => {
    // `status` is a closed enum from the card's <select>.
    void goalMutations.updateGoal(goalId, {
      status: status as PlayerGoal["status"],
    });
  };

  const handleDelete = (goal: PlayerGoal) => {
    setConfirmDeleteGoal(goal);
  };

  const confirmDeleteGoalFn = () => {
    if (!confirmDeleteGoal) return;
    const goal = confirmDeleteGoal;
    setConfirmDeleteGoal(null);
    void goalMutations.deleteGoal(goal.id);
  };

  return (
    <>
      {confirmDeleteGoal ? (
        <ConfirmDialog
          title="Delete goal?"
          message={`Delete the goal "${confirmDeleteGoal.title || "Player goal"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteGoalFn}
          onClose={() => setConfirmDeleteGoal(null)}
          data-testid="coach-development-delete-confirm"
        />
      ) : null}
      <CoachModal
        title={`Development — ${playerName}`}
        onClose={onClose}
        data-testid="coach-development-modal"
      >
      {devQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="coach-development-loading"
        >
          Loading development profile…
        </p>
      ) : devQuery.isError ? (
        <p
          className="text-destructive font-mono text-sm px-3 py-6"
          data-testid="coach-development-error"
        >
          Could not load the development profile.
        </p>
      ) : devQuery.data ? (
        <PlayerDevelopmentProfile
          profile={devQuery.data}
          viewer={false}
          goalsSlot={
            <GoalList
              goals={devQuery.data.active_goals}
              viewer={false}
              onNewGoal={() => onNewGoal(playerId)}
              onChangeStatus={handleStatusChange}
              onEdit={onEditGoal}
              onDelete={handleDelete}
            />
          }
        />
      ) : null}
      </CoachModal>
    </>
  );
}
